// =============================================================================
// Lumen Viewer — canvas 2D DICOM renderer
// =============================================================================
// Separates a cold "source pass" (window → grayscale ImageData) from a warm
// "transform pass" (pan/zoom/rotate) so WC/WW edits during drag don't pay the
// decode cost on every frame.
//
// renderToCanvas(canvas, parsed, opts)
//   opts: { wc, ww, invert, zoom, panX, panY, rotation }
// =============================================================================

export function computeWindowedImageData(parsed, { wc, ww, invert }) {
  const { rows, cols, bitsAllocated, pixelRepresentation,
          rescaleSlope = 1, rescaleIntercept = 0, photometricInterpretation, pixelData } = parsed;

  if (!pixelData) throw new Error('renderer: no pixelData');
  if (!rows || !cols) throw new Error('renderer: missing rows/cols');

  let pixels;
  if (bitsAllocated === 16) {
    pixels = pixelRepresentation === 1
      ? new Int16Array(pixelData.buffer, pixelData.byteOffset, pixelData.length / 2)
      : new Uint16Array(pixelData.buffer, pixelData.byteOffset, pixelData.length / 2);
  } else if (bitsAllocated === 8) {
    pixels = pixelRepresentation === 1
      ? new Int8Array(pixelData.buffer, pixelData.byteOffset, pixelData.length)
      : new Uint8Array(pixelData.buffer, pixelData.byteOffset, pixelData.length);
  } else {
    throw new Error(`renderer: unsupported BitsAllocated=${bitsAllocated}`);
  }

  const lo = wc - ww / 2;
  const mono1 = (photometricInterpretation || '').toUpperCase() === 'MONOCHROME1';
  const flipBit = invert ? !mono1 : mono1; // final XOR

  const img = new ImageData(cols, rows);
  const data = img.data;

  for (let i = 0, j = 0; i < pixels.length && j + 4 <= data.length; i++, j += 4) {
    const real = pixels[i] * rescaleSlope + rescaleIntercept;
    let v = Math.round(((real - lo) / ww) * 255);
    if (v < 0) v = 0; else if (v > 255) v = 255;
    if (flipBit) v = 255 - v;
    data[j] = v; data[j + 1] = v; data[j + 2] = v; data[j + 3] = 255;
  }

  return img;
}

// Draws imageData to a destination canvas with pan/zoom/rotate applied.
// The source is painted on an off-screen canvas once; redraws are cheap.
export function paintTransformed(dest, offscreen, opts) {
  const { zoom = 1, panX = 0, panY = 0, rotation = 0 } = opts || {};
  const ctx = dest.getContext('2d');
  const w = dest.width;
  const h = dest.height;

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.translate(w / 2 + panX, h / 2 + panY);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(zoom, zoom);

  const scale = Math.min(w / offscreen.width, h / offscreen.height);
  const drawW = offscreen.width * scale;
  const drawH = offscreen.height * scale;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'medium';
  ctx.drawImage(offscreen, -drawW / 2, -drawH / 2, drawW, drawH);

  ctx.restore();
}

// Convenience: full render from parsed + opts. Returns the offscreen canvas
// so the caller can cache it and only repaint on WC/WW changes.
export function renderToCanvas(destCanvas, parsed, opts) {
  const imageData = computeWindowedImageData(parsed, opts);
  const offscreen = document.createElement('canvas');
  offscreen.width = parsed.cols;
  offscreen.height = parsed.rows;
  offscreen.getContext('2d').putImageData(imageData, 0, 0);
  paintTransformed(destCanvas, offscreen, opts);
  return offscreen;
}

// Window presets from CLAUDE.md.
export const WINDOW_PRESETS = [
  { name: 'Bone',        wc:  400, ww: 1800 },
  { name: 'Lung',        wc: -600, ww: 1600 },
  { name: 'Brain',       wc:   40, ww:   80 },
  { name: 'Abdomen',     wc:   60, ww:  400 },
  { name: 'Spine',       wc:  400, ww: 1500 },
  { name: 'Soft Tissue', wc:   50, ww:  350 },
];
